import { priorDocumentReference } from '../shared/priorDocumentReference';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type DragEvent,
  type ReactElement,
} from 'react';
import { DragHandle } from '@/components/compiler/DragHandle';
import {
  v2,
  ApiError,
  parseApiErrorBody,
  type BulkConfirmRejectedDetail,
  type Document,
  type Submission,
} from '@/api/v2Client';
import { isClassifiableDocument, isGeneratedDocument } from '@/lib/classifiable';
import { StatusBadge } from '@/components/StatusBadge';
import { EmptyState } from '@/components/dashboard/EmptyState';
import { Spinner } from '@/components/dashboard/Spinner';
import { formatDate } from '@/lib/format';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import {
  resolveDocumentDisplayName,
  useShowOriginalFilenames,
} from '@/hooks/useShowOriginalFilenames';
import { ConfirmBadge } from './ConfirmBadge';
import {
  deriveFileColumnWidth,
  nextFileColumnWidth,
  DEFAULT_MIN_FILE_WIDTH,
} from './fileColumnWidth';
import {
  LIFECYCLE_OPERATIONS,
  LIFECYCLE_VISUAL,
  normalizeLifecycle,
  type LifecycleOperation,
} from '@/components/compiler/lifecycle';
import {
  LifecyclePriorDocPicker,
  type PriorDocument,
} from '@/components/shared/LifecyclePriorDocPicker';
import { ForceConfirmDialog } from './ForceConfirmDialog';
import { DocumentReorderList } from '@/components/DocumentReorderList';
import { DocumentPreviewPane } from '@/components/shared/DocumentPreviewPane';
import { PageToast, type ToastMessage } from '@/components/shared/PageToast';

export interface DocumentsTabPanelProps {
  readonly submission: Submission;
  /**
   * Phase 5.10c — page-level observer. Fired with the current documents
   * list every time it changes (initial load + after classify / confirm /
   * unconfirm / delete / upload-refetch). Lets the parent page derive
   * the compile-gate banner without re-fetching.
   */
  readonly onDocumentsChanged?: (docs: readonly Document[]) => void;
  /**
   * phase-parity-document-operation-staging — true when the sequence is locked
   * / complete. The Operation column is read-only when locked (V1 parity).
   */
  readonly isLocked?: boolean;
}

/** phase-fix-locked-sequence-document-ui — why the actions are gone. */
const LOCKED_ROW_TOOLTIP =
  'This sequence is locked (submitted or completed). Documents cannot be changed unless it is unlocked.';

interface ClassifyState {
  readonly status: 'idle' | 'running' | 'error';
  readonly error?: string;
}

interface ConfirmState {
  readonly status: 'idle' | 'running' | 'error';
  readonly error?: string;
}

/**
 * Phase 5.10f — bulk-confirm progress + summary state for the toolbar
 * "Confirm all (N)" action. Phase 5.10d sequenced per-doc calls; Phase
 * 5.10f migrated to Phase 5.10e's atomic bulk endpoint, so the running
 * state collapses to a single in-flight call (no per-doc progress) and
 * the done state collapses to either all-success, pre-flight rejection,
 * or transport failure.
 */
type BulkConfirmState =
  | { readonly status: 'idle' }
  | { readonly status: 'running'; readonly total: number }
  | {
      readonly status: 'done';
      readonly total: number;
      readonly confirmed: number;
      readonly skipped: number;
      /**
       * Phase 5.10g.2 — whether this bulk-confirm call exercised the admin
       * force-confirm override (Phase 5.10g). Captured at call-time so a
       * mid-flight UI state change can't desync the success message from
       * what the operator actually authorized.
       */
      readonly forced: boolean;
      /**
       * Phase 5.10g.2 — count of unclassified documents force-confirmed
       * on this call (0 when `forced` is false). Drives the
       * "(N unclassified — override recorded in audit log)" parenthetical.
       */
      readonly unclassifiedCount: number;
    }
  | {
      readonly status: 'rejected';
      readonly total: number;
      readonly detail: BulkConfirmRejectedDetail;
      /**
       * Phase 5.10g.3 — re-uses the Phase 5.10g.2 capture so the rejection
       * alert can flag "this was a force-confirm call" even when the call
       * failed pre-flight (e.g. mismatched submission). Admins triaging a
       * failed bulk-confirm need to see the force state alongside the
       * breakdown counts, not after they re-read the original button.
       */
      readonly forced: boolean;
      /**
       * Phase 5.10g.3 — unclassified-doc count at call time, 0 when
       * `forced` is false. Drives the "(N unclassified document(s) were
       * included)" parenthetical on the rejected alert.
       */
      readonly unclassifiedCount: number;
    }
  | {
      readonly status: 'failed';
      readonly total: number;
      readonly message: string;
      /**
       * Phase 5.10g.4 — whether this bulk-confirm call exercised the admin
       * force-confirm override (Phase 5.10g). Captured from `wasForceCall`
       * at the same point in `handleBulkConfirm` that 5.10g.2 (success) and
       * 5.10g.3 (rejected) read — single source of truth across all three
       * terminal states. No `unclassifiedCount` here because the failed
       * branch is generic (transport / 5xx) and the call mode matters more
       * than the per-doc breakdown.
       */
      readonly forced: boolean;
    };

interface UploadEntry {
  readonly tempId: string;
  readonly fileName: string;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

// phase-documents-tab-layout-batch (Item 1) — compact single-line rows at
// the Dashboard table's density (was 12px/top → ~3 text lines per row).
const tdStyle: CSSProperties = { padding: '6px 8px', verticalAlign: 'middle' };

export function DocumentsTabPanel({
  submission,
  onDocumentsChanged,
  isLocked = false,
}: DocumentsTabPanelProps): ReactElement {
  const [docs, setDocs] = useState<Document[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // phase-parity-document-operation-staging — lifecycle Operation editing.
  // `opBusyId` disables a row's control mid-save; `opError` surfaces a server
  // rejection (e.g. the 0000 all-New guard); `picker` holds the doc + operation
  // awaiting a prior-document selection.
  const [opBusyId, setOpBusyId] = useState<string | null>(null);
  const [opError, setOpError] = useState<string | null>(null);
  const [picker, setPicker] = useState<{ doc: Document; operation: LifecycleOperation } | null>(
    null,
  );
  // An original (0000) sequence is all-New: the Operation is fixed and read-only.
  const isOriginalSequence = submission.SequenceNumber === '0000';

  // Merge a lifecycle patch into the local docs list after a successful save.
  const applyLocalLifecycle = (docId: string, patch: Partial<Document>): void => {
    setDocs((prev) => (prev ? prev.map((d) => (d.Id === docId ? { ...d, ...patch } : d)) : prev));
  };

  const readOpError = (err: unknown): string =>
    err instanceof ApiError
      ? (parseApiErrorBody(err)?.message ?? err.message)
      : 'Failed to update the operation';

  // User picked an operation in the row's dropdown. 'new' clears any prior
  // target immediately; replace/append/delete open the prior-document picker.
  // Persistence goes through v2.documents.assignLifecycle (PATCH /documents/:id)
  // — the same endpoint the Compiler inspector uses, so the two can't disagree.
  const handleOperationChange = async (doc: Document, op: LifecycleOperation): Promise<void> => {
    if (isLocked || !canWriteDocuments) return;
    setOpError(null);
    if (op === normalizeLifecycle(doc.LifecycleOperation)) return;
    if (op === 'new') {
      setOpBusyId(doc.Id);
      try {
        const updated = await v2.documents.assignLifecycle(doc.Id, {
          lifecycleOperation: 'new',
          modifiedFile: null,
          replacesDocumentId: null,
        }, doc.RowVersion);
        applyLocalLifecycle(doc.Id, {
          LifecycleOperation: updated.LifecycleOperation,
          ModifiedFile: updated.ModifiedFile ?? null,
          ReplacesDocumentId: updated.ReplacesDocumentId ?? null,
          RowVersion: updated.RowVersion,
        });
      } catch (err) {
        setOpError(readOpError(err));
      } finally {
        setOpBusyId(null);
      }
      return;
    }
    setPicker({ doc, operation: op });
  };

  // A prior document was chosen in the picker. Set the operation + the eCTD
  // ModifiedFile reference (V1's `../../../<seq>/index.xml#<leaf>` form) + the
  // ReplacesDocumentId target in one PATCH. Cancel just closes the picker,
  // leaving the operation unchanged.
  const handlePriorSelected = async (prior: PriorDocument): Promise<void> => {
    if (!picker) return;
    const { doc, operation } = picker;
    const modifiedFile = priorDocumentReference(prior);
    setPicker(null);
    setOpBusyId(doc.Id);
    setOpError(null);
    try {
      const updated = await v2.documents.assignLifecycle(doc.Id, {
        lifecycleOperation: operation,
        modifiedFile,
        replacesDocumentId: prior.Id,
      }, doc.RowVersion);
      applyLocalLifecycle(doc.Id, {
        LifecycleOperation: updated.LifecycleOperation,
        ModifiedFile: updated.ModifiedFile ?? modifiedFile,
        ReplacesDocumentId: updated.ReplacesDocumentId ?? prior.Id,
        RowVersion: updated.RowVersion,
      });
    } catch (err) {
      setOpError(readOpError(err));
    } finally {
      setOpBusyId(null);
    }
  };
  const [classifyStates, setClassifyStates] = useState<Record<string, ClassifyState>>({});
  const [confirmStates, setConfirmStates] = useState<Record<string, ConfirmState>>({});
  // Phase settings-auto-populate-on-confirm (TODO-074) — toast for auto-created forms.
  const [autoFormToast, setAutoFormToast] = useState<ToastMessage | null>(null);
  const [bulkConfirmState, setBulkConfirmState] = useState<BulkConfirmState>({
    status: 'idle',
  });

  // Mutation controls require a positively resolved permission, including
  // the inline uploader and each document row (COV014).
  const permissions = useUserPermissions();
  const canWriteDocuments = !permissions.loading && !permissions.failed && permissions.hasPermission('Documents', 'write');
  const canDeleteDocuments = !permissions.loading && !permissions.failed && permissions.hasPermission('Documents', 'delete');
  const canBulkConfirm = canWriteDocuments;

  // Phase 5.X-v1-parity-polish Feature 1 — user-scope toggle for
  // OriginalFileName vs FileName rendering. See useShowOriginalFilenames.
  const { showOriginal, setShowOriginal } = useShowOriginalFilenames();
  // Phase 5.10g — admin-only force-confirm of unclassified documents.
  // Phase phase-review-surface-strengthen — replaced the single checkbox +
  // window.confirm with a ForceConfirmDialog that requires a typed reason
  // and an explicit acknowledgment before the override is submitted.
  const isAdmin = permissions.hasRole('admin');
  const [forceUnclassified, setForceUnclassified] = useState(false);
  const [showForceDialog, setShowForceDialog] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [uploads, setUploads] = useState<UploadEntry[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // Phase 5.X-screen-review-part-c — reorder mode toggle.
  // When active, replaces the document table with DocumentReorderList
  // so the user can drag documents into a new sort order within the
  // submission. Only shown to users with Documents:write permission.
  const [reorderMode, setReorderMode] = useState(false);

  // Phase UI-12 — sort state for the document table
  type SortCol = 'filename' | 'module' | 'confidence' | 'status';
  const [sortCol, setSortCol] = useState<SortCol>('module');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Phase UI-12 — group by module toggle (shows Pending Review section)
  const [groupByModule, setGroupByModule] = useState(false);

  // Phase UI-12 — preview pane state
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // phase-ui-details-preview-pane-resize (Sharon 2026-07-18) — the old
  // splitter bar advertised col-resize but its only handler was
  // click-to-close: dragging was NEVER implemented (born inert, not broken
  // by a later change). Real dragging now reuses the Compiler's proven
  // DragHandle. The preview width is pixels, owned here, clamped by the
  // handle, persisted per browser session; null = not yet measured →
  // default to HALF the content area (the 45-55% band) on first open.
  const PREVIEW_WIDTH_STORAGE_KEY = 'autoectd-details-preview-width';
  const MIN_PREVIEW_WIDTH = 460;
  const MIN_TABLE_WIDTH = 500;
  const splitContainerRef = useRef<HTMLDivElement | null>(null);
  const [previewWidth, setPreviewWidth] = useState<number | null>(() => {
    try {
      const stored = window.sessionStorage.getItem(PREVIEW_WIDTH_STORAGE_KEY);
      const n = stored === null ? NaN : Number.parseInt(stored, 10);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  });
  const applyPreviewWidth = useCallback((px: number) => {
    setPreviewWidth(px);
    try {
      window.sessionStorage.setItem(PREVIEW_WIDTH_STORAGE_KEY, String(Math.round(px)));
    } catch {
      /* session-only state is fine */
    }
  }, []);
  // phase-documents-pane-split-content-sized — `previewWidth !== null` now
  // means the USER has dragged an explicit split (persisted per session, the
  // ccd0ac7 override). When it's null the split is CONTENT-SIZED: the table
  // is only as wide as its columns need and the pane absorbs the rest. The
  // old "default to 58%" effect is gone — a fresh open is content-sized.
  const userHasDragged = previewWidth !== null;

  // Measured width of the split container — for the File Name max cap (~40%)
  // and the pane's floor math. Measured on open + resize (NOT per scroll/hover)
  // so content derivation stays stable.
  const [containerWidth, setContainerWidth] = useState<number>(0);
  useEffect(() => {
    if (!previewOpen) return;
    const measure = (): void => {
      const w = splitContainerRef.current?.clientWidth ?? 0;
      if (w > 0) setContainerWidth(w);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [previewOpen]);

  // T07-D9 — the panel's auto-dismiss / auto-collapse timers. Each slot holds
  // at most one pending timer: scheduling replaces (clears) the previous one,
  // and every pending timer is cleared on unmount, so none can fire after the
  // panel is gone. The delays and what each timer does are unchanged.
  type DismissSlot = 'bulkConfirm' | 'classifyAll' | 'uploadCollapse';
  const dismissTimers = useRef<Partial<Record<DismissSlot, ReturnType<typeof globalThis.setTimeout>>>>({});
  const scheduleDismiss = useCallback((slot: DismissSlot, ms: number, fn: () => void): void => {
    const previous = dismissTimers.current[slot];
    if (previous !== undefined) globalThis.clearTimeout(previous);
    dismissTimers.current[slot] = globalThis.setTimeout(() => {
      delete dismissTimers.current[slot];
      fn();
    }, ms);
  }, []);
  useEffect(() => {
    const timers = dismissTimers.current;
    return () => {
      for (const t of Object.values(timers)) if (t !== undefined) globalThis.clearTimeout(t);
    };
  }, []);

  const stackPreview = previewOpen && containerWidth > 0 && containerWidth < 800;
  const previewMaxWidth = Math.max(
    MIN_PREVIEW_WIDTH,
    (containerWidth || splitContainerRef.current?.clientWidth || 1200) - MIN_TABLE_WIDTH - 6,
  );

  // Phase UI-12 — classify-all SSE progress
  type ClassifyAllState =
    | { status: 'idle' }
    | { status: 'running'; completed: number; total: number; errors: number }
    | { status: 'done'; completed: number; total: number; errors: number }
    | { status: 'error'; message: string };
  const [classifyAllState, setClassifyAllState] = useState<ClassifyAllState>({
    status: 'idle',
  });

  useEffect(() => {
    setDocs(null);
    setError(null);
    v2.documents
      .listBySubmission(submission.Id)
      .then(setDocs)
      .catch((err: unknown) => {
        setError(
          err instanceof ApiError
            ? readableApiMessage(err, 'Failed to load documents')
            : 'Failed to load documents',
        );
        setDocs([]);
      });
  }, [submission.Id, reloadKey]);

  // Phase 5.10c — bubble the docs list to the page so it can render the
  // compile-gate banner / disable the package CTA. Skips the null
  // (loading) state so the page doesn't briefly clear its derived state
  // on every reloadKey bump.
  useEffect(() => {
    if (docs !== null) onDocumentsChanged?.(docs);
  }, [docs, onDocumentsChanged]);

  const counts = useMemo(() => {
    const list = docs ?? [];
    // Phase 5.10b — separate "classified but unconfirmed" from "confirmed".
    // M = AI-classified, awaiting user confirmation.
    // K = user-confirmed (the workflow's terminal state for a document).
    const confirmed = list.filter((d) => Boolean(d.ConfirmedAt)).length;
    const classified = list.filter(
      (d) => Boolean(d.Classification) && !d.ConfirmedAt,
    ).length;
    return { total: list.length, classified, confirmed };
  }, [docs]);

  // ----- Classify (preserves Phase 5.6 behavior exactly) -----

  const handleClassify = async (docId: string): Promise<void> => {
    if (isLocked || !canWriteDocuments) return;
    setClassifyStates((prev) => ({ ...prev, [docId]: { status: 'running' } }));
    try {
      const res = await v2.documents.classify(docId);
      setDocs((prev) =>
        prev
          ? prev.map((d) =>
              d.Id === docId
                ? {
                    ...d,
                    Classification: res.Classification,
                    ClassificationConfidence: res.ClassificationConfidence,
                    ClassificationRationale: res.ClassificationRationale,
                    ClassifiedBy: res.ClassifiedBy,
                    ClassifiedAt: new Date().toISOString(),
                  }
                : d,
            )
          : prev,
      );
      setClassifyStates((prev) => ({ ...prev, [docId]: { status: 'idle' } }));
    } catch (err) {
      // Surface the underlying error message (ApiError or any Error) so
      // users see why classification failed (timeout, quota, 404, etc.)
      // instead of a generic "Classify failed" string. Matches Phase 5.6.
      const message =
        err instanceof ApiError
          ? readableApiMessage(err, 'Classify failed')
          : err instanceof Error
            ? err.message
            : 'Classify failed';
      setClassifyStates((prev) => ({
        ...prev,
        [docId]: { status: 'error', error: message },
      }));
    }
  };

  // ----- Confirm / Unconfirm (Phase 5.10b) -----
  //
  // Optimistic UI: stamp ConfirmedAt/ConfirmedBy from the API response.
  // On error, revert to the pre-call state and surface the message inline.

  // phase-fix-locked-sequence-document-ui — an ApiError's .message is often
  // the RAW JSON body; prefer the parsed body message, keep plain-text
  // messages, and only fall back when the message itself is JSON noise.
  const readableApiMessage = (err: ApiError, fallback: string): string =>
    parseApiErrorBody(err)?.message ??
    (err.message.trimStart().startsWith('{') ? fallback : err.message);
  const extractErrorMessage = (err: unknown, fallback: string): string =>
    err instanceof ApiError
      ? readableApiMessage(err, fallback)
      : err instanceof Error
        ? err.message
        : fallback;

  const handleConfirm = async (docId: string): Promise<void> => {
    if (isLocked || !canWriteDocuments) return;
    setConfirmStates((prev) => ({ ...prev, [docId]: { status: 'running' } }));
    try {
      const response = await v2.documents.confirm(docId);
      setDocs((prev) =>
        prev
          ? prev.map((d) =>
              d.Id === docId
                ? {
                    ...d,
                    ConfirmedAt: response.data.ConfirmedAt,
                    ConfirmedBy: response.data.ConfirmedBy,
                  }
                : d,
            )
          : prev,
      );
      // Phase settings-auto-populate-on-confirm (TODO-074) — show a toast
      // when the backend auto-created a form entry for this document.
      if (response.autoCreatedForm) {
        setAutoFormToast({
          type: 'success',
          text: `Auto-populated ${response.autoCreatedForm.displayName} — review it in the Forms tab.`,
        });
      }
      // Phase auto-sync-cover-letter (TODO-005 + TODO-074) — show a brief
      // toast when cover letter regeneration was triggered in the background.
      if (response.coverLetterSynced) {
        setAutoFormToast({
          type: 'success',
          text: 'Cover letter updated.',
        });
      }
      setConfirmStates((prev) => ({ ...prev, [docId]: { status: 'idle' } }));
    } catch (err) {
      setConfirmStates((prev) => ({
        ...prev,
        [docId]: { status: 'error', error: extractErrorMessage(err, 'Confirm failed') },
      }));
    }
  };

  const handleUnconfirm = async (docId: string): Promise<void> => {
    if (isLocked || !canWriteDocuments) return;
    setConfirmStates((prev) => ({ ...prev, [docId]: { status: 'running' } }));
    try {
      await v2.documents.unconfirm(docId);
      setDocs((prev) =>
        prev
          ? prev.map((d) =>
              d.Id === docId
                ? { ...d, ConfirmedAt: null, ConfirmedBy: null }
                : d,
            )
          : prev,
      );
      setConfirmStates((prev) => ({ ...prev, [docId]: { status: 'idle' } }));
    } catch (err) {
      setConfirmStates((prev) => ({
        ...prev,
        [docId]: { status: 'error', error: extractErrorMessage(err, 'Unconfirm failed') },
      }));
    }
  };

  // ----- Bulk Confirm (Phase 5.10f) -----
  //
  // Single atomic call to POST /api/v1/submissions/:id/documents/bulk-confirm
  // (Phase 5.10e). All-or-nothing semantics: a 200 means every classified
  // target confirmed; a 400 BulkConfirmRejected means none did and the
  // server returned a structured breakdown of which ids were notFound /
  // notClassified / mismatchedSubmission. Per-row error badges from the
  // 5.10d sequential loop are no longer needed in the bulk flow because
  // the failure mode is transactional — single-confirm still uses them.

  const unconfirmedClassifiedDocs = useMemo(
    () =>
      (docs ?? []).filter((d) => Boolean(d.Classification) && !d.ConfirmedAt),
    [docs],
  );

  // Phase 5.10g — unclassified docs are the targets of an admin force-confirm.
  // A doc with `ConfirmedAt` set is treated as already-handled even if it
  // lacks Classification (defensive — shouldn't happen in practice).
  const unclassifiedDocs = useMemo(
    () => (docs ?? []).filter((d) => !d.Classification && !d.ConfirmedAt),
    [docs],
  );

  // Only honor the force checkbox when both visibility conditions hold:
  // user is admin AND there's at least one unclassified doc. Belt-and-
  // suspenders — the checkbox itself doesn't render outside these
  // conditions, but a stale `forceUnclassified=true` state (e.g. after
  // an unclassified doc got reclassified mid-flight) should not silently
  // expand the targets.
  const forceActive = isAdmin && forceUnclassified && unclassifiedDocs.length > 0;

  // Phase phase-review-surface-strengthen — extracted the actual API call so
  // it can be called from both the normal confirm path and after the force
  // dialog is confirmed. The force dialog is now the entry point for
  // force-confirms; this function handles the common API call logic.
  const executeConfirm = async (wasForceCall: boolean): Promise<void> => {
    const unclassifiedAtCallTime = wasForceCall ? unclassifiedDocs.length : 0;
    const targets = wasForceCall
      ? [...unconfirmedClassifiedDocs, ...unclassifiedDocs]
      : unconfirmedClassifiedDocs;
    if (targets.length === 0) return;
    const documentIds = targets.map((t) => t.Id);
    setBulkConfirmState({ status: 'running', total: targets.length });

    try {
      const result = await v2.documents.bulkConfirm(
        submission.Id,
        { documentIds },
        wasForceCall ? { force: true } : {},
      );
      // Stamp ConfirmedAt/ConfirmedBy in-place for every id in result.confirmed[].
      // The backend doesn't echo per-doc rows for the bulk call so we use a
      // client-side timestamp + the actor's email; the next reload picks up
      // the canonical server values. result.skipped[] ids were already
      // confirmed — their existing fields are already correct.
      const nowIso = new Date().toISOString();
      const confirmedSet = new Set(result.confirmed);
      const userEmail = permissions.email ?? 'you';
      setDocs((prev) =>
        prev
          ? prev.map((d) =>
              confirmedSet.has(d.Id)
                ? { ...d, ConfirmedAt: nowIso, ConfirmedBy: userEmail }
                : d,
            )
          : prev,
      );
      setBulkConfirmState({
        status: 'done',
        total: targets.length,
        confirmed: result.confirmed.length,
        skipped: result.skipped.length,
        forced: wasForceCall,
        unclassifiedCount: unclassifiedAtCallTime,
      });
      // Auto-dismiss the green summary after 5s.
      scheduleDismiss('bulkConfirm', 5000, () => {
        setBulkConfirmState({ status: 'idle' });
      });
    } catch (err) {
      const parsed = parseApiErrorBody(err);
      if (parsed?.error === 'BulkConfirmRejected' && parsed.data) {
        setBulkConfirmState({
          status: 'rejected',
          total: targets.length,
          detail: parsed.data as BulkConfirmRejectedDetail,
          // Phase 5.10g.3 — re-use the same capture pair the success path
          // uses (Phase 5.10g.2). Single source of truth means the success
          // and rejected branches can't disagree about what was attempted.
          forced: wasForceCall,
          unclassifiedCount: unclassifiedAtCallTime,
        });
        return;
      }
      const message =
        parsed?.message ?? extractErrorMessage(err, 'Bulk confirm failed');
      setBulkConfirmState({
        status: 'failed',
        total: targets.length,
        message,
        // Phase 5.10g.4 — re-use the same call-time capture the success
        // (5.10g.2) and rejected (5.10g.3) branches read. All three
        // terminal alerts now agree about what call mode the admin
        // authorized, regardless of subsequent UI state changes.
        forced: wasForceCall,
      });
    }
  };

  // Phase phase-review-surface-strengthen — entry point for the bulk-confirm
  // button. Force-active calls open the dialog; non-force calls go directly.
  const handleBulkConfirm = (): void => {
    if (isLocked || !canWriteDocuments) return;
    const targets = forceActive
      ? [...unconfirmedClassifiedDocs, ...unclassifiedDocs]
      : unconfirmedClassifiedDocs;
    if (targets.length === 0) return;
    if (forceActive) {
      setShowForceDialog(true);
    } else {
      void executeConfirm(false);
    }
  };

  const handleDeleteDocument = async (docId: string): Promise<void> => {
    if (isLocked || !canDeleteDocuments) return;
    if (!confirm('Soft-delete this document?')) return;
    try {
      await v2.documents.delete(docId);
      setReloadKey((k) => k + 1);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? readableApiMessage(err, 'Failed to delete document')
          : 'Failed to delete document';
      alert(msg);
    }
  };

  // ----- Sort (Phase UI-12) -----

  const handleSortClick = (col: SortCol): void => {
    if (sortCol === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const sortIndicator = (col: SortCol): string => {
    if (sortCol !== col) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  };

  // ----- Classify All (Phase UI-12) -----

  const handleClassifyAll = async (): Promise<void> => {
    if (isLocked || !canWriteDocuments) return;
    const unclassifiedIds = (docs ?? [])
      .filter(isClassifiableDocument)
      .map((d) => d.Id);
    if (unclassifiedIds.length === 0) return;
    setClassifyAllState({ status: 'running', completed: 0, total: unclassifiedIds.length, errors: 0 });

    try {
      const res = await v2.submissions.classifyAllStream(submission.Id, unclassifiedIds);
      if (!res.ok) {
        // phase-fix-classify-all-eligibility (Defect 2) — classify-all reads a
        // RAW fetch Response (SSE), so unlike the apiFetch-based sibling actions
        // it must decode the refusal itself. It used to render the raw body
        // (`HTTP 422: {"error":"NothingToClassify",…}` in red — Sharon's
        // screenshot). Route it through the same readable-refusal helper the
        // other actions use so the friendly message ("All documents … already
        // classified.") shows, never raw JSON.
        const text = await res.text().catch(() => '');
        setClassifyAllState({
          status: 'error',
          message: readableApiMessage(new ApiError(res.status, text, text), 'Classify all failed'),
        });
        return;
      }

      // phase-fix-classify-all-eligibility — the route returns a JOB
      // ({ jobId, totalDocs }) and classifies in the background; there is no SSE
      // body. (The old code parsed `res.body` as an SSE stream — dead against
      // the job route: it always fell through to "0/N processed". It was never
      // exercised because eligibility used to refuse before any success.) Poll
      // the status endpoint to completion, then refetch the documents so the
      // rows show their new classifications.
      const started = (await res.json().catch(() => null)) as
        | { data?: { totalDocs?: number } }
        | null;
      const total = started?.data?.totalDocs ?? unclassifiedIds.length;
      setClassifyAllState({ status: 'running', completed: 0, total, errors: 0 });

      // Bounded poll — ~2 min ceiling at 1s cadence so a stuck job can't spin
      // forever. Progress (0–100) maps to a completed/total display.
      const MAX_POLLS = 120;
      for (let i = 0; i < MAX_POLLS; i += 1) {
        await new Promise((r) => globalThis.setTimeout(r, 1000));
        let job: Awaited<ReturnType<typeof v2.submissions.classifyAllStatus>>;
        try {
          job = await v2.submissions.classifyAllStatus(submission.Id);
        } catch {
          continue; // transient — keep polling
        }
        const completed = Math.round(((job.progress ?? 0) / 100) * total);
        if (job.status === 'complete' || job.status === 'error') {
          setReloadKey((k) => k + 1); // refetch docs → rows reflect new classifications
          if (job.status === 'error') {
            setClassifyAllState({
              status: 'error',
              message:
                'Classify all finished with errors on some documents. Reloaded — check the statuses below.',
            });
          } else {
            setClassifyAllState({ status: 'done', completed: total, total, errors: 0 });
            scheduleDismiss('classifyAll', 4000, () => setClassifyAllState({ status: 'idle' }));
          }
          return;
        }
        setClassifyAllState({ status: 'running', completed, total, errors: 0 });
      }
      // Timed out waiting — refetch so the UI shows whatever landed.
      setReloadKey((k) => k + 1);
      setClassifyAllState({
        status: 'error',
        message: 'Classify all is taking longer than expected. Reloaded — check the statuses below.',
      });
    } catch (err) {
      setClassifyAllState({
        status: 'error',
        message:
          err instanceof ApiError
            ? readableApiMessage(err, 'Classify all failed')
            : err instanceof Error
              ? err.message
              : 'Classify all failed',
      });
    }
  };

  // ----- Upload -----

  const processFile = async (file: File): Promise<void> => {
    const tempId = `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setUploads((prev) => [
      ...prev,
      { tempId, fileName: file.name, status: 'pending' },
    ]);
    // T-17 (T17-D5) — `create` records a PENDING upload that becomes a document
    // only when the upload succeeds; drop it if the upload fails (best-effort).
    let pendingId: string | null = null;
    try {
      setUploads((prev) =>
        prev.map((u) => (u.tempId === tempId ? { ...u, status: 'uploading' } : u)),
      );
      const created = await v2.documents.create({
        SubmissionId: submission.Id,
        FileName: file.name,
        LifecycleOperation: 'new',
      });
      pendingId = created.Id;
      await v2.documents.uploadFile(created.Id, file);
      pendingId = null;
      setUploads((prev) =>
        prev.map((u) => (u.tempId === tempId ? { ...u, status: 'done' } : u)),
      );
    } catch (err) {
      if (pendingId) {
        void v2.documents.abandonPendingUpload(pendingId).catch(() => {
          /* best-effort cleanup */
        });
      }
      setUploads((prev) =>
        prev.map((u) =>
          u.tempId === tempId
            ? {
                ...u,
                status: 'error',
                error: err instanceof ApiError ? err.message : 'Upload failed',
              }
            : u,
        ),
      );
    }
  };

  const handleFiles = async (files: readonly File[]): Promise<void> => {
    if (isLocked || !canWriteDocuments) return;
    if (files.length === 0) return;
    await Promise.all(files.map(processFile));
    setReloadKey((k) => k + 1);
    // Auto-collapse after all uploads finish — defer slightly so the user
    // sees the final "done" state before the zone closes.
    scheduleDismiss('uploadCollapse', 800, () => {
      setUploads([]);
      setUploadOpen(false);
    });
  };

  const onDragOver = (e: DragEvent<HTMLElement>): void => {
    e.preventDefault();
    setDragging(true);
  };
  const onDragLeave = (): void => setDragging(false);
  const onDrop = (e: DragEvent<HTMLElement>): void => {
    e.preventDefault();
    setDragging(false);
    void handleFiles(Array.from(e.dataTransfer.files));
  };

  // ----- Sorted list (Phase UI-12) -----

  const sortedList = useMemo(() => {
    const raw = docs ?? [];
    return [...raw].sort((a, b) => {
      let av = '';
      let bv = '';
      if (sortCol === 'filename') {
        av = (a.OriginalFileName ?? a.FileName ?? '').toLowerCase();
        bv = (b.OriginalFileName ?? b.FileName ?? '').toLowerCase();
      } else if (sortCol === 'module') {
        av = a.Classification ?? a.ModuleNumber ?? '';
        bv = b.Classification ?? b.ModuleNumber ?? '';
      } else if (sortCol === 'confidence') {
        const ac = a.ClassificationConfidence ?? -1;
        const bc = b.ClassificationConfidence ?? -1;
        return sortDir === 'asc' ? ac - bc : bc - ac;
      } else if (sortCol === 'status') {
        av = a.ConfirmedAt ? 'confirmed' : a.Classification ? 'classified' : 'unclassified';
        bv = b.ConfirmedAt ? 'confirmed' : b.Classification ? 'classified' : 'unclassified';
      }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [docs, sortCol, sortDir]);

  // Phase UI-12 — preview doc for the preview pane
  const previewDoc = useMemo(
    () => (previewDocId ? sortedList.find((d) => d.Id === previewDocId) ?? null : null),
    [previewDocId, sortedList],
  );

  const previewDocIndex = previewDoc ? sortedList.findIndex((d) => d.Id === previewDoc.Id) : -1;

  // phase-documents-pane-split-content-sized — CONTENT-SIZED File Name column
  // (pane-open default). Derive from the rendered filenames ONCE per data
  // change (names/sort/showOriginal), NOT per scroll/hover; jitter-gated so a
  // one-character change doesn't nudge the boundary. `sortedList` IS the
  // rendered set (groupByModule only reorders within it), and the display
  // name honours the OriginalFileName toggle, matching the cell.
  const visibleNamesKey = useMemo(
    () =>
      sortedList
        .map((d) =>
          resolveDocumentDisplayName(
            { FileName: d.FileName, OriginalFileName: d.OriginalFileName },
            showOriginal,
          ),
        )
        .join(' '),
    [sortedList, showOriginal],
  );
  // phase-forms-and-documents-review-batch (item 1) — Operation is
  // regulatory-meaningful (new/replace/append + the prior-linked note) and
  // STAYS visible when the preview pane is open (only Uploaded drops). So the
  // pane-open fixed-column total now includes Operation's 120px — this keeps
  // the content-sized split (8c1dd21) correct: File Name still gets the
  // leftover width after Module + Status + Operation + Actions.
  const FIXED_COLS_OPEN = 72 + 150 + 120 + 248; // Module + Status + Operation + Actions
  const targetFileColWidth = useMemo(() => {
    const names = visibleNamesKey.length === 0 ? [] : visibleNamesKey.split(' ');
    // Max cap ≈ 40% of the split area (stable, container-based — not circular
    // on the pane width). Falls back to a sane cap before first measurement.
    const maxWidth = Math.max(
      DEFAULT_MIN_FILE_WIDTH,
      Math.round((containerWidth || 1200) * 0.4),
    );
    return deriveFileColumnWidth(names, { maxWidth });
     
  }, [visibleNamesKey, containerWidth]);
  const [fileColWidth, setFileColWidth] = useState<number | null>(null);
  useEffect(() => {
    if (!previewOpen) return;
    setFileColWidth((cur) => nextFileColumnWidth(cur, targetFileColWidth));
  }, [previewOpen, targetFileColWidth]);
  // The content-sized left (table) track width: File Name + the fixed columns.
  const contentTableWidth = (fileColWidth ?? targetFileColWidth) + FIXED_COLS_OPEN;

  // ----- Render -----

  if (docs === null && !error) {
    return <Spinner caption="Loading documents…" />;
  }

  if (error) {
    return (
      <div role="alert" style={{ color: 'var(--red)', padding: 16 }}>
        {error}
        <button
          type="button"
          onClick={() => setReloadKey((k) => k + 1)}
          style={{ marginLeft: 12, padding: '4px 12px', cursor: 'pointer' }}
        >
          Retry
        </button>
      </div>
    );
  }

  // ---- Phase 5.10f — bulk-confirm summary alert (above the toolbar) ----
  //
  // Three terminal states render here: `done` (all-success green),
  // `rejected` (amber pre-flight breakdown with categorised id counts),
  // `failed` (red transport/unknown error). The `running` state shows in
  // the button label, not as a separate alert.
  const bulkAlert = ((): ReactElement | null => {
    if (bulkConfirmState.status === 'done') {
      const { confirmed, skipped, forced, unclassifiedCount } = bulkConfirmState;
      // Phase 5.10g.2 — when the call was a force-confirm AND actually
      // included unclassified docs, surface that explicitly. The "forced"
      // parenthetical wins over "already confirmed" — admins need to see
      // their override was exercised more than they need to see skip counts.
      let note = '';
      if (forced && unclassifiedCount > 0) {
        note = ` (${unclassifiedCount} unclassified — override recorded in audit log)`;
      } else if (skipped > 0) {
        note = ` (${skipped} were already confirmed)`;
      }
      return (
        <div
          role="status"
          data-testid="bulk-confirm-summary"
          style={{
            background: 'var(--green-bg, #DCFCE7)',
            color: 'var(--green, #15803D)',
            border: '1px solid var(--green, #15803D)',
            borderRadius: 6,
            padding: '8px 12px',
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          Confirmed {confirmed} document{confirmed === 1 ? '' : 's'}
          {note}.
        </div>
      );
    }
    if (bulkConfirmState.status === 'rejected') {
      const { detail, forced, unclassifiedCount } = bulkConfirmState;
      const parts: string[] = [];
      const nf = detail.notFound?.length ?? 0;
      const nc = detail.notClassified?.length ?? 0;
      const mm = detail.mismatchedSubmission?.length ?? 0;
      if (nf) parts.push(`${nf} not found`);
      if (nc) parts.push(`${nc} not classified`);
      if (mm) parts.push(`${mm} mismatched submission`);
      const breakdown = parts.length > 0 ? parts.join(', ') : 'unknown reason';
      return (
        <div
          role="alert"
          data-testid="bulk-confirm-summary"
          style={{
            background: 'var(--amber-bg, #FEF3C7)',
            color: 'var(--amber, #B45309)',
            border: '1px solid var(--amber, #B45309)',
            borderRadius: 6,
            padding: '8px 12px',
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          Bulk-confirm rejected: {breakdown}. No documents were changed.
          {/* Phase 5.10g.3 — show the force-call state inline so admins
              triaging the rejection don't have to look back at the button.
              Rendered as a separate block so screen readers + sighted users
              both treat it as additional context, not a continuation of
              the main rejection sentence. */}
          {forced && (
            <span
              data-testid="bulk-confirm-rejected-forced-note"
              style={{
                display: 'block',
                fontSize: 12,
                opacity: 0.85,
                marginTop: 4,
              }}
            >
              Force-confirm was active for this call
              {unclassifiedCount > 0
                ? ` (${unclassifiedCount} unclassified document${unclassifiedCount === 1 ? '' : 's'} were included).`
                : '.'}
            </span>
          )}
        </div>
      );
    }
    if (bulkConfirmState.status === 'failed') {
      const { message, forced } = bulkConfirmState;
      return (
        <div
          role="alert"
          data-testid="bulk-confirm-summary"
          style={{
            background: 'var(--red-bg, #FEE2E2)',
            color: 'var(--red, #B91C1C)',
            border: '1px solid var(--red, #B91C1C)',
            borderRadius: 6,
            padding: '8px 12px',
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          Bulk-confirm failed: {message}
          {forced && (
            <span
              data-testid="bulk-confirm-failed-forced-note"
              style={{ display: 'block', fontSize: 12, opacity: 0.85, marginTop: 4 }}
            >
              Force-confirm was active for this call.
            </span>
          )}
        </div>
      );
    }
    return null;
  })();

  const bulkRunning = bulkConfirmState.status === 'running';
  const bulkButtonLabel = bulkRunning
    ? `Confirming ${bulkConfirmState.total}…`
    : forceActive
      ? `Confirm all (${unconfirmedClassifiedDocs.length} + ${unclassifiedDocs.length} unclassified)`
      : `Confirm all (${unconfirmedClassifiedDocs.length})`;
  const showBulkButton =
    canBulkConfirm &&
    (unconfirmedClassifiedDocs.length > 0 || forceActive || bulkRunning);
  // Phase 5.10g — show the force-confirm affordance only for admins with
  // at least one unclassified doc on the current submission.
  const showForceCheckbox = isAdmin && unclassifiedDocs.length > 0;

  // Phase UI-12 — classify all progress banner
  const classifyAllBanner = ((): ReactElement | null => {
    if (classifyAllState.status === 'running') {
      return (
        <div
          role="status"
          data-testid="classify-all-banner"
          style={{
            background: 'var(--amber-bg, #FEF3C7)',
            color: 'var(--amber, #B45309)',
            border: '1px solid var(--amber, #B45309)',
            borderRadius: 6,
            padding: '8px 12px',
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          Classifying… {classifyAllState.completed}/{classifyAllState.total}
          {classifyAllState.errors > 0 && ` (${classifyAllState.errors} errors)`}
        </div>
      );
    }
    if (classifyAllState.status === 'done') {
      return (
        <div
          role="status"
          data-testid="classify-all-banner"
          style={{
            background: 'var(--green-bg, #DCFCE7)',
            color: 'var(--green, #15803D)',
            border: '1px solid var(--green, #15803D)',
            borderRadius: 6,
            padding: '8px 12px',
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          Classify all complete: {classifyAllState.completed}/{classifyAllState.total} processed
          {classifyAllState.errors > 0 && ` (${classifyAllState.errors} errors)`}
        </div>
      );
    }
    if (classifyAllState.status === 'error') {
      return (
        <div
          role="alert"
          data-testid="classify-all-banner"
          style={{
            background: 'var(--red-bg, #FEE2E2)',
            color: 'var(--red, #B91C1C)',
            border: '1px solid var(--red, #B91C1C)',
            borderRadius: 6,
            padding: '8px 12px',
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          Classify all failed: {classifyAllState.message}
        </div>
      );
    }
    return null;
  })();

  const list = docs ?? [];
  const unclassifiedCount = list.filter(isClassifiableDocument).length;

  return (
    <div style={{ padding: 16 }}>
      {/* Phase phase-review-surface-strengthen — force-confirm friction dialog */}
      {showForceDialog && (
        <ForceConfirmDialog
          unclassifiedCount={unclassifiedDocs.length}
          classifiedCount={unconfirmedClassifiedDocs.length}
          onCancel={() => setShowForceDialog(false)}
          onConfirm={(_reason) => {
            setShowForceDialog(false);
            void executeConfirm(true);
          }}
        />
      )}
      {/* Phase settings-auto-populate-on-confirm (TODO-074) — auto-form toast */}
      <PageToast
        toast={autoFormToast}
        onDismiss={() => setAutoFormToast(null)}
        dismissAfterMs={6000}
        data-testid="auto-form-toast"
      />
      {bulkAlert}
      {classifyAllBanner}
      {/* phase-parity-document-operation-staging — prior-document picker +
          server-rejection surface for lifecycle operation assignment. */}
      {picker && (
        <LifecyclePriorDocPicker
          submissionId={submission.Id}
          currentDocumentId={picker.doc.Id}
          operation={picker.operation}
          sectionCode={picker.doc.CtdSectionCode ?? picker.doc.Classification ?? null}
          onSelect={(prior) => void handlePriorSelected(prior)}
          onCancel={() => setPicker(null)}
        />
      )}
      {opError && (
        <div
          role="alert"
          data-testid="operation-error"
          style={{
            color: 'var(--red, #b91c1c)',
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.35)',
            borderRadius: 6,
            padding: '8px 12px',
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          {opError}
        </div>
      )}
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 12,
          flexWrap: 'wrap',
        }}
      >
        {!isLocked && !permissions.loading && !canWriteDocuments && (
          <span role="note" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {permissions.failed ? 'Editing permissions could not be checked.' : 'Your role can view documents; document editing is unavailable.'}
          </span>
        )}
        {isLocked && (
          <span
            data-testid="documents-locked-note"
            title={LOCKED_ROW_TOOLTIP}
            style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}
          >
            🔒 This sequence is locked — documents are read-only.
          </span>
        )}
        <button
          type="button"
          onClick={() => setUploadOpen((o) => !o)}
          hidden={isLocked || !canWriteDocuments}
          aria-expanded={uploadOpen}
          aria-controls="documents-upload-zone"
          style={{
            background: 'var(--accent)',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '8px 14px',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {uploadOpen ? '× Close upload' : '+ Upload documents'}
        </button>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginLeft: 'auto',
            flexWrap: 'wrap',
          }}
        >
          {/* Phase 5.X-v1-parity-polish Feature 1 — show-original-filenames toggle */}
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: 'var(--text-muted)',
              cursor: 'pointer',
              userSelect: 'none',
            }}
            data-testid="show-original-filenames-toggle-wrap"
          >
            <input
              type="checkbox"
              checked={showOriginal}
              onChange={(e) => setShowOriginal(e.target.checked)}
              data-testid="show-original-filenames-toggle"
              aria-label="Show original file names"
            />
            Show original names
          </label>
          {/* Phase phase-review-surface-strengthen — force-confirm toggle.
              Replaced the single-click checkbox (which was too easy to
              accidentally check). Now requires an explicit activation to
              enable the force mode, then clicking Confirm All opens a
              dedicated dialog that requires a typed reason + ack. */}
          {!isLocked && canWriteDocuments && showForceCheckbox && (
            <button
              type="button"
              data-testid="force-confirm-toggle"
              title={
                forceUnclassified
                  ? 'Override armed — Confirm All will ALSO confirm the unclassified documents (a typed reason is required, and the action is audited). Click to disarm.'
                  : 'Normally Confirm All only confirms AI-classified documents. This admin override lets it also confirm the unclassified ones (requires a typed reason; audited).'
              }
              onClick={() => setForceUnclassified((v) => !v)}
              disabled={bulkRunning}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                padding: '4px 10px',
                borderRadius: 4,
                border: `1px solid ${forceUnclassified ? 'rgba(239,68,68,0.60)' : 'var(--border)'}`,
                background: forceUnclassified ? 'rgba(239,68,68,0.08)' : 'transparent',
                color: forceUnclassified ? '#B91C1C' : 'var(--text-muted)',
                cursor: bulkRunning ? 'not-allowed' : 'pointer',
                fontWeight: forceUnclassified ? 600 : 400,
              }}
            >
              {/* phase-fix-submission-details-polish (review §2 #11) — "⚠ Force
                  off (N unclassified)" said what it was, not what it does. */}
              {forceUnclassified
                ? `⚠ Override ON — Confirm All will include ${unclassifiedDocs.length} unclassified`
                : `Admin override: include ${unclassifiedDocs.length} unclassified in Confirm All`}
            </button>
          )}
          {!isLocked && showBulkButton && (
            <button
              type="button"
              onClick={handleBulkConfirm}
              disabled={bulkRunning}
              data-testid="bulk-confirm-btn"
              title={
                forceActive
                  ? 'Confirm every AI-classified document AND the unclassified documents (admin override — audited).'
                  : 'Confirm every AI-classified document that hasn’t been confirmed yet'
              }
              style={{
                background: 'transparent',
                color: 'var(--accent)',
                border: '1px solid var(--accent)',
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: bulkRunning ? 'not-allowed' : 'pointer',
                opacity: bulkRunning ? 0.7 : 1,
              }}
            >
              {bulkButtonLabel}
            </button>
          )}
          {/* Phase 5.X-screen-review-part-c — reorder mode toggle */}
          {canBulkConfirm && (
            <button
              type="button"
              data-testid="reorder-mode-toggle"
              hidden={isLocked || !canWriteDocuments}
              onClick={() => setReorderMode((m) => !m)}
              title={reorderMode ? 'Exit reorder mode' : 'Drag to reorder documents within this submission'}
              style={{
                background: reorderMode ? 'var(--accent)' : 'transparent',
                color: reorderMode ? '#fff' : 'var(--accent)',
                border: '1px solid var(--accent)',
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {reorderMode ? '✓ Done reordering' : '⠿ Reorder'}
            </button>
          )}
          {/* Phase UI-12 — Group by module toggle */}
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: 'var(--text-muted)',
              cursor: 'pointer',
              userSelect: 'none',
            }}
            data-testid="group-by-module-toggle-wrap"
          >
            <input
              type="checkbox"
              checked={groupByModule}
              onChange={(e) => setGroupByModule(e.target.checked)}
              data-testid="group-by-module-toggle"
              aria-label="Group by module"
            />
            Group by module
          </label>
          {/* Phase UI-12 — Classify All button */}
          {!isLocked && canWriteDocuments && unclassifiedCount > 0 && classifyAllState.status !== 'running' && (
            <button
              type="button"
              onClick={() => { void handleClassifyAll(); }}
              data-testid="classify-all-btn"
              style={{
                background: 'transparent',
                color: 'var(--accent)',
                border: '1px solid var(--accent)',
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Classify all ({unclassifiedCount})
            </button>
          )}
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {counts.total} document{counts.total === 1 ? '' : 's'} · {counts.classified} classified ·{' '}
            {counts.confirmed} confirmed
          </span>
        </div>
      </div>

      {/* Collapsible upload zone */}
      {uploadOpen && !isLocked && canWriteDocuments && (
        <div id="documents-upload-zone" style={{ marginBottom: 16 }}>
          <div
            role="button"
            tabIndex={0}
            className={`dropzone${dragging ? ' drag-over' : ''}`}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
            }}
            data-testid="upload-dropzone"
            aria-label="Upload documents"
          >
            <div className="dropzone-icon" aria-hidden="true">
              ⬆
            </div>
            <p>
              <strong>Click to browse</strong> or drag and drop files here
            </p>
            <small>PDF / DOCX / XML / TXT — one document per file</small>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={(e) => {
                if (e.target.files) void handleFiles(Array.from(e.target.files));
              }}
              style={{ display: 'none' }}
              aria-label="Choose files to upload"
              data-testid="upload-file-input"
            />
          </div>
          {uploads.length > 0 && (
            <ul style={{ marginTop: 12, fontSize: 12, listStyle: 'none', padding: 0 }}>
              {uploads.map((u) => (
                <li
                  key={u.tempId}
                  data-testid={`upload-${u.tempId}`}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '4px 8px',
                    background: 'var(--surface2)',
                    borderRadius: 4,
                    marginBottom: 4,
                  }}
                >
                  <span>{u.fileName}</span>
                  <span
                    style={{
                      color:
                        u.status === 'error'
                          ? 'var(--red)'
                          : u.status === 'done'
                            ? 'var(--green)'
                            : 'var(--text-muted)',
                    }}
                  >
                    {u.status === 'error' ? u.error ?? 'error' : u.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Document table or empty state */}
      {/* Phase 5.X-screen-review-part-c — reorder mode replaces the table */}
      {reorderMode && list.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
            Drag rows to change the display order within this submission. Changes are saved immediately.
          </p>
          <DocumentReorderList
            submissionId={submission.Id}
            documents={list}
            onReordered={(newOrder) => setDocs([...newOrder])}
            renderRow={(doc) => (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 16, color: 'var(--text-muted)', cursor: 'grab' }}>⠿</span>
                <span style={{ flex: 1, fontSize: 13 }}>
                  {resolveDocumentDisplayName(
                    { FileName: doc.FileName, OriginalFileName: doc.OriginalFileName },
                    showOriginal,
                  )}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {doc.Classification ?? '—'}
                </span>
              </div>
            )}
          />
        </div>
      )}
      {/* Phase UI-12 — split-pane layout when preview open */}
      {!reorderMode && (
        <div
          ref={splitContainerRef}
          style={{
            display: 'grid',
            // phase-documents-pane-split-content-sized — two open-pane modes:
            //  • DRAGGED (userHasDragged): the ccd0ac7 model — pane fixed at
            //    the user's px (clamped to previewMaxWidth), table flexes into
            //    the remainder.
            //  • DEFAULT (content-sized): the TABLE track is capped at the
            //    width its columns need (contentTableWidth) but can SHRINK
            //    below it (minmax(0, …) — ISS-016), and the PANE (1fr,
            //    floored at MIN_PREVIEW_WIDTH) absorbs all leftover width — so
            //    short filenames automatically widen the PDF while a narrow
            //    viewport truncates the table instead of overflowing the body.
            gridTemplateColumns: stackPreview ? 'minmax(0, 1fr)' : previewOpen
              ? userHasDragged
                ? `minmax(0, 1fr) 6px ${Math.min(previewWidth ?? 0, previewMaxWidth)}px`
                : `minmax(0, ${contentTableWidth}px) 6px minmax(${MIN_PREVIEW_WIDTH}px, 1fr)`
              : '1fr',
            gap: stackPreview ? 12 : 0,
            // ISS-016 — the default table track was a FIXED content-sized px
            // width, so contentTableWidth + the pane's 460px floor could exceed
            // the container (1280px viewport → 1575px body, horizontal scroll).
            // The track is now a shrinkable CAP (minmax(0, Xpx)): the table
            // truncates (fixed layout + ellipsis, already its convention) and
            // the pane keeps its floor; the pane is also clamped against a
            // stale dragged width after a viewport shrink. maxWidth keeps the
            // grid itself from ever exceeding its parent.
            maxWidth: '100%',
            // phase-documents-tab-layout-batch (Item 3) — when the preview is
            // open the split fills the available vertical space to near the
            // viewport bottom so an A4 page isn't a short letterbox.
            minHeight: previewOpen
              ? 'calc(100vh - 240px)'
              : list.length === 0
                ? undefined
                : 400,
          }}
        >
          {/* Left pane: table */}
          <div className="submission-document-table-area" style={{ minWidth: 0, containerType: 'inline-size' }}>
            {list.length === 0 ? (
              <EmptyState
                icon="📄"
                heading="No documents yet"
                subtext={isLocked ? 'This sequence is locked and contains no documents. Create a new sequence to add documents.' : 'Upload regulatory documents to start the eCTD assembly process.'}
                {...(!isLocked && canWriteDocuments ? { action: {
                  label: '+ Upload your first document',
                  onClick: () => setUploadOpen(true),
                } } : {})}
              />
            ) : (
              <>
                {/* Phase UI-12 — Pending Review section when groupByModule is on */}
                {groupByModule && ((): ReactElement | null => {
                  const pending = sortedList.filter((d) => !d.ConfirmedAt);
                  if (pending.length === 0) return null;
                  return (
                    <div style={{ marginBottom: 16 }}>
                      <div
                        data-testid="pending-review-header"
                        style={{
                          padding: '6px 8px',
                          background: 'var(--amber-bg, #FEF3C7)',
                          color: 'var(--amber, #B45309)',
                          fontSize: 12,
                          fontWeight: 700,
                          borderRadius: '4px 4px 0 0',
                          border: '1px solid var(--amber, #B45309)',
                          borderBottom: 'none',
                        }}
                      >
                        Pending Review ({pending.length})
                      </div>
                      <table className="submission-document-table" data-preview-open={previewOpen} style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                          {pending.map((d) => renderDocRow(d))}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
                <table className="submission-document-table" data-preview-open={previewOpen} style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                  {/* phase-ui-details-preview-pane-resize — dashboard-stability
                      convention: fixed layout + explicit columns so dragging
                      the divider narrows the table gracefully (filename
                      truncates) instead of reflowing every column. */}
                  <colgroup data-testid="documents-colgroup">
                    {/* File name — phase-documents-pane-split-content-sized:
                        content-sized when the pane is open in the DEFAULT
                        (non-dragged) mode so leftover width feeds the pane;
                        flex otherwise (pane closed, or the user dragged and
                        the table flexes into the remainder). Either way it
                        truncates with ellipsis + title tooltip past its cap. */}
                    {previewOpen && !userHasDragged ? (
                      <col
                        data-testid="documents-col-filename"
                        style={{ width: fileColWidth ?? targetFileColWidth }}
                      />
                    ) : (
                      <col data-testid="documents-col-filename" />
                    )}
                    <col style={{ width: 72 }} />{/* Module */}
                    <col style={{ width: 150 }} />{/* Status */}
                    <col style={{ width: 120 }} />{/* Operation — kept pane-open (item 1) */}
                    {!previewOpen && <col style={{ width: 104 }} />}{/* Uploaded */}
                    <col style={{ width: 248 }} />{/* Actions — fixed, always inside */}
                  </colgroup>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
                      <th
                        style={{ ...tdStyle, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSortClick('filename')}
                        data-testid="sort-col-filename"
                      >
                        File name{sortIndicator('filename')}
                      </th>
                      <th
                        style={{ ...tdStyle, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSortClick('module')}
                        data-testid="sort-col-module"
                      >
                        Module{sortIndicator('module')}
                      </th>
                      <th
                        style={{ ...tdStyle, cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => handleSortClick('status')}
                        data-testid="sort-col-status"
                      >
                        Status{sortIndicator('status')}
                      </th>
                      <th style={tdStyle}>Operation</th>
                      {!previewOpen && <th style={tdStyle}>Uploaded</th>}
                      <th style={tdStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(groupByModule ? sortedList.filter((d) => Boolean(d.ConfirmedAt)) : sortedList).map(
                      (d) => renderDocRow(d),
                    )}
                  </tbody>
                </table>
              </>
            )}
          </div>

          {/* phase-ui-details-preview-pane-resize — a REAL divider (the
              Compiler's DragHandle: drag + keyboard + aria-separator + clamp).
              Closing the preview stays on the pane's own ✕ button. */}
          {previewOpen && !stackPreview && (
            <DragHandle
              direction="right"
              // phase-documents-pane-split-content-sized — in the default
              // (non-dragged) mode the pane is the content-sized remainder, so
              // a drag starts from THAT actual width (not a 50% guess), then
              // applyPreviewWidth pins the user's split for the session.
              currentWidth={
                previewWidth ??
                Math.max(
                  MIN_PREVIEW_WIDTH,
                  (containerWidth || splitContainerRef.current?.clientWidth || 1200) -
                    contentTableWidth -
                    6,
                )
              }
              minWidth={MIN_PREVIEW_WIDTH}
              maxWidth={previewMaxWidth}
              onChange={applyPreviewWidth}
              ariaLabel="Resize preview pane"
            />
          )}

          {/* Right pane: preview — minWidth:0 lets the grid shrink the track;
              maxWidth:100% keeps the pane inside it (ISS-016). */}
          {previewOpen && previewDoc && (
            <div style={{ height: stackPreview ? 'calc(100vh - 240px)' : '100%', minHeight: stackPreview ? 560 : undefined, minWidth: 0, maxWidth: '100%' }}>
              {!previewDoc.Classification && !previewDoc.ConfirmedAt && canWriteDocuments && !isLocked && (
                <p role="status" data-testid="preview-classification-required">
                  Choose and approve a section before confirming this document.{' '}
                  <a href={`/submissions/${submission.Id}/compiler`}>Open the compiler to classify it manually.</a>
                </p>
              )}
              {confirmStates[previewDoc.Id]?.error && <p role="alert">{confirmStates[previewDoc.Id]?.error}</p>}
              <DocumentPreviewPane
                locked={isLocked}
                documentId={previewDoc.Id}
                documentName={resolveDocumentDisplayName(
                  { FileName: previewDoc.FileName, OriginalFileName: previewDoc.OriginalFileName },
                  showOriginal,
                )}
                // phase-fix-seed-docs-missing-files — the file/content routes
                // gate on Sha256Hash. Mirror that so a document with no uploaded
                // file shows an explanatory state, not the raw NotFound JSON.
                hasFile={Boolean(previewDoc.Sha256Hash ?? previewDoc.FileUploadedAt)}
                classificationStatus={previewDoc.ConfirmedAt ? 'Confirmed' : null}
                classificationConfidence={
                  typeof previewDoc.ClassificationConfidence === 'number'
                    ? Math.round(previewDoc.ClassificationConfidence * 100)
                    : null
                }
                {...(canWriteDocuments && {
                  ...(previewDoc.Classification ? { onApprove: () => { void handleConfirm(previewDoc.Id); } } : {}),
                  onReject: () => { void handleUnconfirm(previewDoc.Id); },
                })}
                onClose={() => setPreviewOpen(false)}
                onPrev={() => {
                  if (previewDocIndex > 0) {
                    const prev = sortedList[previewDocIndex - 1];
                    if (prev) setPreviewDocId(prev.Id);
                  }
                }}
                onNext={() => {
                  if (previewDocIndex < sortedList.length - 1) {
                    const next = sortedList[previewDocIndex + 1];
                    if (next) setPreviewDocId(next.Id);
                  }
                }}
                hasPrev={previewDocIndex > 0}
                hasNext={previewDocIndex < sortedList.length - 1}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ----- renderDocRow helper -----
  function renderDocRow(d: Document): ReactElement {
    const classify = classifyStates[d.Id] ?? { status: 'idle' as const };
    const confirmS = confirmStates[d.Id] ?? { status: 'idle' as const };
    const canClassify = Boolean(d.Sha256Hash);
    const hasClassification = Boolean(d.Classification);
    const isConfirmed = Boolean(d.ConfirmedAt);
    const isSelected = previewDocId === d.Id && previewOpen;
    return (
      <tr
        key={d.Id}
        style={{
          borderBottom: '1px solid var(--border)',
          background: isSelected ? 'var(--surface2)' : undefined,
          cursor: 'pointer',
        }}
        data-testid={`doc-row-${d.Id}`}
        onClick={(e) => {
          // phase-forms-and-documents-review-batch (item 7) — the row opens the
          // preview, but ANY interactive in-row control must NOT bubble to it.
          // The old guard only excluded button/anchor, so the Operation <select>
          // (and inputs/labels/textarea) leaked the click and opened the pane
          // while operating the control. Exclude every interactive element.
          if ((e.target as HTMLElement).closest('button,a,select,input,textarea,label')) return;
          setPreviewDocId(d.Id);
          setPreviewOpen(true);
        }}
      >
        <td
          data-label="File name"
          style={{ ...tdStyle, whiteSpace: 'normal', overflowWrap: 'anywhere' }}
          title={resolveDocumentDisplayName(
            d as { FileName: string; OriginalFileName?: string | null },
            showOriginal,
          )}
          data-testid={`doc-filename-${d.Id}`}
        >
          {resolveDocumentDisplayName(
            d as { FileName: string; OriginalFileName?: string | null },
            showOriginal,
          )}
        </td>
        <td data-label="Module" style={tdStyle}>{d.Classification ?? d.ModuleNumber ?? '—'}</td>
        <td data-label="Status" style={tdStyle} data-testid={`classification-${d.Id}`}>
          {isConfirmed ? (
            // Attribution (who + when) is audit detail, not scanning info —
            // moved to the capsule's title tooltip; Lifecycle History keeps
            // the full record.
            <span
              title={`Confirmed by ${d.ConfirmedBy ?? 'unknown'} on ${formatDate(d.ConfirmedAt)}`}
              data-testid={`status-attribution-${d.Id}`}
            >
              <ConfirmBadge state="confirmed" />
            </span>
          ) : hasClassification ? (
            <span style={{ whiteSpace: 'nowrap' }}>
              <ConfirmBadge state="ai-classified" />
              {typeof d.ClassificationConfidence === 'number' && (
                <span
                  style={{ marginLeft: 6, color: 'var(--text-muted)', fontSize: 12 }}
                >
                  {Math.round(d.ClassificationConfidence * 100)}%
                </span>
              )}
            </span>
          ) : (
            // phase-fix-submission-details-polish (review §2 #5) — the row
            // badge is WORKFLOW state: a document with no classification is
            // awaiting classification, full stop. Whether its file content
            // (hash) is present only gates the Classify ACTION, whose
            // disabled tooltip already explains that.
            <StatusBadge status="Classify" />
          )}
          {classify.status === 'error' && classify.error && (
            <div role="alert" style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>
              {classify.error}
            </div>
          )}
          {confirmS.status === 'error' && confirmS.error && (
            <div
              role="alert"
              data-testid={`confirm-error-${d.Id}`}
              style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}
            >
              {confirmS.error}
            </div>
          )}
        </td>
        {/* phase-parity-document-operation-staging — eCTD lifecycle Operation.
            Editable inline on an unlocked supplement; read-only badge on an
            original (0000, always New) or a locked sequence.
            phase-forms-and-documents-review-batch (item 1) — Operation is
            regulatory-meaningful and STAYS when the preview pane is open
            (reversing ccd0ac7's pane-open drop; only Uploaded drops). */}
        <td data-label="Operation" style={tdStyle} data-testid={`operation-cell-${d.Id}`}>
          {(() => {
            const op = normalizeLifecycle(d.LifecycleOperation);
            const rawUnset =
              !d.LifecycleOperation || String(d.LifecycleOperation).trim() === '';
            const vis = LIFECYCLE_VISUAL[op];
            const editable = !isLocked && canWriteDocuments && !isOriginalSequence;
            const busy = opBusyId === d.Id;
            const badge = (label: string, v: typeof vis): ReactElement => (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  padding: '1px 6px',
                  borderRadius: 4,
                  background: v.background,
                  color: v.color,
                  border: `1px solid ${v.border}`,
                  textDecoration: v.strikethrough ? 'line-through' : 'none',
                }}
              >
                {label}
              </span>
            );
            if (!editable) {
              // Original is all-New; locked shows the frozen current operation.
              const shown = isOriginalSequence ? LIFECYCLE_VISUAL.new : vis;
              return badge(isOriginalSequence ? 'NEW' : vis.label, shown);
            }
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <select
                  data-testid={`operation-select-${d.Id}`}
                  value={op}
                  disabled={busy}
                  onChange={(e) =>
                    void handleOperationChange(d, e.target.value as LifecycleOperation)
                  }
                  style={{
                    fontSize: 11,
                    padding: '2px 6px',
                    borderRadius: 4,
                    border: `1px solid ${rawUnset ? 'var(--red, #dc2626)' : 'var(--border)'}`,
                    background: 'var(--surface)',
                    color: 'var(--text)',
                    cursor: busy ? 'wait' : 'pointer',
                    width: 90,
                  }}
                >
                  {LIFECYCLE_OPERATIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
                {rawUnset && (
                  <span
                    data-testid={`operation-unset-${d.Id}`}
                    style={{ fontSize: 10, color: 'var(--red, #dc2626)', fontWeight: 600 }}
                  >
                    — not set
                  </span>
                )}
                {op !== 'new' && d.ReplacesDocumentId && (
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>↳ prior linked</span>
                )}
              </div>
            );
          })()}
        </td>
        {/* Uploaded — the ONLY column that drops when the pane is open (item 1). */}
        {!previewOpen && (
        <td data-label="Uploaded" style={{ ...tdStyle, fontSize: 12, color: 'var(--text-muted)' }}>
          {formatDate(d.FileUploadedAt ?? d.CreatedAt)}
        </td>
        )}
        <td data-label="Actions" style={{ ...tdStyle, whiteSpace: 'normal' }}>
          {/* phase-fix-locked-sequence-document-ui — a locked sequence's rows
              are READ-ONLY: no action the server would 403 is offered. */}
          {isLocked ? (
            <span
              data-testid={`row-locked-${d.Id}`}
              title={LOCKED_ROW_TOOLTIP}
              style={{ fontSize: 12, color: 'var(--text-muted)' }}
            >
              🔒 Locked
            </span>
          ) : (
            <>
          {canWriteDocuments && hasClassification && !isConfirmed && (
            <button
              type="button"
              onClick={() => handleConfirm(d.Id)}
              disabled={confirmS.status === 'running'}
              data-testid={`confirm-btn-${d.Id}`}
              title="Confirm this AI classification"
              style={{
                color: 'var(--green, #15803D)',
                background: 'none',
                border: '1px solid currentColor',
                borderRadius: 4,
                padding: '4px 10px',
                marginRight: 8,
                cursor: confirmS.status === 'running' ? 'not-allowed' : 'pointer',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {confirmS.status === 'running' ? 'Confirming…' : 'Confirm'}
            </button>
          )}
          {canWriteDocuments && isConfirmed && (
            <button
              type="button"
              onClick={() => handleUnconfirm(d.Id)}
              disabled={confirmS.status === 'running'}
              data-testid={`unconfirm-btn-${d.Id}`}
              title="Revert to AI-classified state"
              style={{
                color: 'var(--text-muted)',
                background: 'none',
                border: '1px solid currentColor',
                borderRadius: 4,
                padding: '4px 10px',
                marginRight: 8,
                cursor: confirmS.status === 'running' ? 'not-allowed' : 'pointer',
                fontSize: 12,
              }}
            >
              {confirmS.status === 'running' ? 'Unconfirming…' : 'Unconfirm'}
            </button>
          )}
          {/* phase-classification-stage-0-fixes item 1 — a GENERATED document
              has no classify affordance at all: its placement is
              template-assigned and authoritative. */}
          {canWriteDocuments && !isGeneratedDocument(d) && (
          <button
            type="button"
            onClick={() => handleClassify(d.Id)}
            disabled={
              !canClassify || classify.status === 'running' || isConfirmed
            }
            title={
              !canClassify
                ? 'Upload the file before classifying'
                : isConfirmed
                  ? 'Unconfirm first to re-classify'
                  : hasClassification
                    ? 'Re-classify this document'
                    : 'Classify this document with AI'
            }
            style={{
              color:
                canClassify && !isConfirmed
                  ? 'var(--accent)'
                  : 'var(--text-muted)',
              background: 'none',
              border: '1px solid currentColor',
              borderRadius: 4,
              padding: '4px 10px',
              marginRight: 8,
              cursor:
                canClassify && classify.status !== 'running' && !isConfirmed
                  ? 'pointer'
                  : 'not-allowed',
              fontSize: 12,
            }}
          >
            {classify.status === 'running'
              ? 'Classifying…'
              : hasClassification
                ? 'Re-classify'
                : 'Classify with AI'}
          </button>
          )}
          {canDeleteDocuments && <button
            type="button"
            onClick={() => handleDeleteDocument(d.Id)}
            style={{
              color: 'var(--red)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            Delete
          </button>}
            </>
          )}
        </td>
      </tr>
    );
  }
}
